import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import Icon from '../../components/AppIcon';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { useToast } from '../../context/ToastContext';
import { fetchComplianceRequests, updateComplianceRequest } from '../../lib/adminApi';

const statusFilters = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

const statusStyles = {
  pending: 'bg-amber-100 text-amber-700',
  approved: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-rose-100 text-rose-700',
};

const formatDoc = (doc) => {
  if (!doc) return null;
  if (typeof doc === 'string') {
    return { label: doc.split('/').pop(), url: doc };
  }
  return {
    label: doc.name || doc.label || doc.url || 'Document',
    url: doc.url || doc.path || '',
  };
};

export default function ComplianceRequestsPage() {
  const { profile } = useAdminAuth();
  const toast = useToast();
  const [statusFilter, setStatusFilter] = useState('all');
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionState, setActionState] = useState({});

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setError(null);
    fetchComplianceRequests({ status: statusFilter === 'all' ? undefined : statusFilter })
      .then((data) => { if (isMounted) setRequests(data); })
      .catch((err) => { if (isMounted) setError(err?.message || 'Failed to load compliance requests'); })
      .finally(() => { if (isMounted) setLoading(false); });
    return () => { isMounted = false; };
  }, [statusFilter]);

  const handleUpdate = async (requestId, nextStatus) => {
    if (!profile?.user_id) return;
    const isReject = nextStatus === 'rejected';
    const rejectionReason = isReject ? prompt('Reason for rejection:') : null;
    if (isReject && !rejectionReason) return;
    const adminNotes = isReject ? null : prompt('Admin notes (optional):') || null;
    setActionState((prev) => ({ ...prev, [requestId]: true }));
    try {
      await updateComplianceRequest(
        { requestId, status: nextStatus, rejectionReason, adminNotes },
        profile.user_id
      );
      setRequests((prev) => prev.map((r) =>
        r.id === requestId
          ? { ...r, status: nextStatus, rejection_reason: rejectionReason, admin_notes: adminNotes, reviewed_at: new Date().toISOString() }
          : r
      ));
      toast.success(`Compliance request ${nextStatus}`);
    } catch (err) {
      toast.error(err?.message || 'Failed to update request');
    } finally {
      setActionState((prev) => ({ ...prev, [requestId]: false }));
    }
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Compliance Requests</h2>
          <p className="text-sm text-muted-foreground">
            Review vendor compliance documents and update verification status.
          </p>
        </div>
      </header>

      {error && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
          {error}
        </div>
      )}

      <div className="flex flex-wrap gap-2 border-b border-border pb-4">
        {statusFilters.map((f) => (
          <button
            key={f.value}
            onClick={() => setStatusFilter(f.value)}
            className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
              statusFilter === f.value ? 'bg-primary text-primary-foreground shadow' : 'bg-muted text-muted-foreground hover:text-foreground'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="rounded-xl border border-border bg-card shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-border text-sm">
            <thead className="bg-muted/30 text-muted-foreground">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Caterer</th>
                <th className="px-4 py-3 text-left font-semibold">Documents</th>
                <th className="px-4 py-3 text-left font-semibold">Status</th>
                <th className="px-4 py-3 text-left font-semibold">Submitted</th>
                <th className="px-4 py-3 text-right font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/80">
              {loading && (
                <tr>
                  <td colSpan={5} className="px-6 py-8 text-center text-muted-foreground">Loading…</td>
                </tr>
              )}
              {!loading && !requests.length && (
                <tr>
                  <td colSpan={5} className="px-6 py-8 text-center text-muted-foreground">No compliance requests found.</td>
                </tr>
              )}
              {!loading && requests.map((req) => {
                const docs = Array.isArray(req.documents) ? req.documents.map(formatDoc).filter(Boolean) : [];
                return (
                  <tr key={req.id} className="hover:bg-muted/30">
                    <td className="px-4 py-3">
                      <div className="flex flex-col">
                        <span className="font-medium text-foreground">{req.caterer?.business_name ?? '—'}</span>
                        <span className="text-xs text-muted-foreground">{req.caterer?.email ?? '—'}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {docs.length > 0 ? (
                        <details>
                          <summary className="cursor-pointer text-sm text-primary">View {docs.length} docs</summary>
                          <ul className="mt-2 space-y-1 text-xs">
                            {docs.map((doc, idx) => (
                              <li key={`${doc.url}-${idx}`}>
                                {doc.url ? (
                                  <a className="text-primary hover:underline" href={doc.url} target="_blank" rel="noreferrer">
                                    {doc.label}
                                  </a>
                                ) : (
                                  <span>{doc.label}</span>
                                )}
                              </li>
                            ))}
                          </ul>
                        </details>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                        statusStyles[req.status] ?? 'bg-muted text-foreground'
                      }`}>
                        {req.status ?? 'pending'}
                      </span>
                      {req.rejection_reason && (
                        <p className="mt-1 text-xs text-muted-foreground" title={req.rejection_reason}>
                          {req.rejection_reason}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {req.submitted_at ? format(new Date(req.submitted_at), 'dd MMM yyyy') : '—'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-2">
                        <button
                          disabled={actionState[req.id] || req.status === 'approved'}
                          onClick={() => handleUpdate(req.id, 'approved')}
                          className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-1 text-xs font-semibold text-white hover:bg-emerald-500 disabled:opacity-60"
                        >
                          <Icon name="BadgeCheck" size={14} /> Approve
                        </button>
                        <button
                          disabled={actionState[req.id] || req.status === 'rejected'}
                          onClick={() => handleUpdate(req.id, 'rejected')}
                          className="inline-flex items-center gap-1 rounded-md bg-rose-600 px-3 py-1 text-xs font-semibold text-white hover:bg-rose-500 disabled:opacity-60"
                        >
                          <Icon name="Ban" size={14} /> Reject
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
